/** Wire types for the office service (services/office). */

export type OfficeStatus =
  | "available"
  | "focus"
  | "meeting"
  | "break"
  | "away"
  | "offline";

/** Statuses a person may pick. `meeting` and `offline` are system-derived. */
export const MANUAL_STATUSES = ["available", "focus", "break", "away"] as const;
export type ManualStatus = (typeof MANUAL_STATUSES)[number];

export type ZoneType = "open" | "focus" | "meeting" | "break";
export type ZoneAccess = "open" | "private";
export type Facing = "down" | "up" | "left" | "right";

export interface Zone {
  id: string;
  name: string;
  type: ZoneType;
  x: number;
  y: number;
  width: number;
  height: number;
  capacity: number;
  access: ZoneAccess;
  color: string;
  /** Whether this zone carries a speak point. Only open areas and breakrooms may. */
  announce_enabled?: boolean;
  /** Absolute tile coordinates; null means the centre of the zone. */
  announce_x?: number | null;
  announce_y?: number | null;
  announce_radius?: number;
}

/** Which zone types may host a speak point — mirrors the server's own rule. */
export const ZONE_TYPES_WITH_SPEAK_POINT: ZoneType[] = ["open", "break"];

export function zoneCanHostSpeakPoint(type: ZoneType): boolean {
  return ZONE_TYPES_WITH_SPEAK_POINT.includes(type);
}

export interface DeskLayout {
  id: string;
  x: number;
  y: number;
  zone_id: string;
  label: string;
  assigned_user_id: string | null;
}

export interface FloorSummary {
  id: string;
  name: string;
  width?: number;
  height?: number;
  is_default: boolean;
  sort_order?: number;
  occupants: number;
}

export interface MapLayout {
  id: string;
  name: string;
  is_default?: boolean;
  sort_order?: number;
  width: number;
  height: number;
  tile_size: number;
  proximity_radius: number;
  revision: number;
  zones: Zone[];
  desks: DeskLayout[];
  announcement_circles?: AnnouncementCircle[];
}

export interface AnnouncementCircle {
  zone_id: string;
  /** The room it addresses — the circle is labelled with it. */
  zone_name?: string;
  x: number;
  y: number;
  radius: number;
}

export interface CurrentTask {
  id: string;
  title: string;
  repo: string | null;
  number: number | null;
  url: string | null;
  manual: boolean;
}

export interface PresenceUser {
  user_id: string;
  sub: string;
  name: string;
  email: string | null;
  avatar_url: string;
  job_title: string;
  team: string;
  roles: string[];
  status: OfficeStatus;
  status_message: string;
  current_task: CurrentTask | null;
  /** `title` is null when the person has not opted into sharing event titles. */
  meeting: { title: string | null; starts_at: string; ends_at: string } | null;
  desk_id: string | null;
  zone_id: string | null;
  /** Which floor they are on, and the room they are standing in, in words. */
  office_map_id: string | null;
  office_map_name: string | null;
  zone_name: string | null;
  position: { x: number; y: number; facing: Facing };
  on_leave_today: boolean;
  session_seconds?: number;
  today_seconds?: number;
  as_of: string;
}

export interface TimerSnapshot {
  session_seconds: number;
  today_seconds: number;
  week_seconds: number;
  break_seconds_today: number;
  status: OfficeStatus;
  paused: boolean;
  as_of: string;
}

export interface UserSummary {
  id: string;
  sub: string;
  name: string;
  email: string | null;
  avatar_url: string;
  job_title: string;
  team: string;
  roles: string[];
  is_active: boolean;
}

export interface UserDetail extends UserSummary {
  timezone: string;
  manager: UserSummary | null;
  reports: UserSummary[];
  share_calendar_titles: boolean;
  github_username: string;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  floors: number;
}

export interface MeResponse {
  user: UserDetail;
  permissions: string[];
  /** WeCo super admins may manage floors in any organization. */
  is_super_admin?: boolean;
  presence: PresenceUser;
  timer: TimerSnapshot;
  layout_revision: number;
}

// ------------------------------------------------------------------- leave

export type LeaveType = "annual" | "sick" | "unpaid" | "parental" | "compassionate";
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancelled";
export type DayPart = "full" | "morning" | "afternoon";

export interface LeavePolicy {
  id: string;
  leave_type: LeaveType;
  label: string;
  accrual_days: string;
  accrual_period: "monthly" | "annual" | "none";
  max_balance_days: string | null;
  carryover_days: string;
  requires_approval: boolean;
  allows_negative_balance: boolean;
  min_notice_days: number;
  is_active: boolean;
}

export interface LeaveBalance {
  id: string;
  user: UserSummary | null;
  leave_type: LeaveType;
  year: number;
  accrued_days: string;
  used_days: string;
  adjustment_days: string;
  carried_over_days: string;
  available_days: string;
}

export interface LeaveRequest {
  id: string;
  user: UserSummary;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  day_part: DayPart;
  days: string;
  note: string;
  status: LeaveStatus;
  decided_by: UserSummary | null;
  decided_at: string | null;
  decision_note: string;
  is_override: boolean;
  cancelled_at: string | null;
  created_at: string;
}

export interface LeaveCalendarEntry {
  id: string;
  user_id: string;
  name: string;
  avatar_url: string;
  team: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  day_part: DayPart;
}

// ----------------------------------------------------------------- reports

export interface DailyReport {
  id: string;
  user: UserSummary;
  date: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
  worked_seconds: number;
  worked_hours: number;
  break_seconds: number;
  focus_seconds: number;
  meeting_seconds: number;
  away_seconds: number;
  on_leave: boolean;
  late_minutes: number;
  note: string;
}

export interface RollupPeriod {
  start: string;
  end: string;
  worked_seconds: number;
  worked_hours: number;
  break_seconds: number;
  focus_seconds: number;
  meeting_seconds: number;
  days_present: number;
  days_on_leave: number;
  late_days: number;
}

export interface BoardRow {
  user_id: string;
  name: string;
  email: string | null;
  avatar_url: string;
  team: string;
  status: OfficeStatus;
  status_message: string;
  zone: string | null;
  session_seconds: number;
  today_seconds: number;
  first_seen_at: string | null;
  on_leave_today: boolean;
}

export interface LiveBoard {
  rows: BoardRow[];
  counters: Record<OfficeStatus, number>;
  server_time: string;
}

export interface OrgMetrics {
  headcount: number;
  online_now: number;
  week_worked_seconds: number;
  week_worked_hours: number;
  average_hours_per_person: number;
  on_leave_today: number;
  /** Rendered verbatim in the UI — it is part of the payload, not decoration. */
  caveat: string;
}

// ------------------------------------------------------------------ social

export interface Knock {
  id: string;
  from_user: UserSummary;
  to_user: UserSummary;
  intent: "chat" | "call";
  reason: string;
  status: "pending" | "accepted" | "declined" | "expired";
  expires_at: string;
  created_at: string;
}

export interface Announcement {
  id: string;
  sender: UserSummary;
  scope: "org" | "team";
  team: string;
  title: string;
  body: string;
  audio_url: string;
  requires_ack: boolean;
  audience_count: number;
  ack_count: number;
  acknowledged_by_me: boolean;
  created_at: string;
}

export interface OfficeNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  href: string;
  read_at: string | null;
  created_at: string;
}

export interface OfficeTask {
  id: string;
  repo: string;
  number: number | null;
  title: string;
  url: string;
  state: "open" | "closed";
  labels: string[];
  is_pull_request: boolean;
  is_manual: boolean;
  is_focus: boolean;
  external_updated_at: string | null;
}

export interface OrgSettings {
  idle_threshold_seconds: number;
  late_grace_minutes: number;
  workday_start: string | null;
  workday_end: string | null;
  proximity_radius: number;
  knock_enabled: boolean;
  knock_ttl_seconds: number;
  breaks_are_paid: boolean;
  announce_circle_requires_manager: boolean;
  github_sync_enabled: boolean;
  calendar_sync_enabled: boolean;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// --------------------------------------------------------------- analytics

export type {
  AnalyticsScope,
  AnalyticsQuery,
  AnalyticsEnvelope,
  DashboardKpis,
  AnalyticsDashboard,
  AttendanceRow,
  CompositionRow,
  UtilizationRow,
  RollupGroup,
  TasksLoad,
  LeaveCoverage,
  OccupancyData,
  Insight,
  InsightsEnvelope,
} from "../../../components/analytics/types";

// ------------------------------------------------------------- the workday

/** Where today's check-in stands (services/office `/me/workday/`). */
export type WorkdayStatus = "none" | "in" | "out";

export interface PersonRef {
  id: string;
  name: string;
  avatar_url: string;
}

export interface WorkdayDesk {
  id: string;
  label: string;
  number: number;
  pod: string;
  zone_name: string;
  floor_id: string;
  floor_name: string;
  /** Set when somebody else (an admin) seated you here. */
  assigned_by: PersonRef | null;
}

export interface Workday {
  status: WorkdayStatus;
  /** The user's own calendar date, YYYY-MM-DD. */
  day: string;
  first_checked_in_at: string | null;
  checked_in_at: string | null;
  checked_out_at: string | null;
  /** At the desk today, every stretch included, as of `as_of`. */
  at_desk_seconds: number;
  onboarded: boolean;
  desk: WorkdayDesk | null;
  desks_total: number;
  desks_free: number;
  waitlisted: boolean;
  as_of: string;
}

export interface FloorDesk {
  id: string;
  label: string;
  number: number;
  pod: string;
  zone_name: string;
  owner: PersonRef | null;
  mine: boolean;
}

export interface FloorDesks {
  floor: { id: string; name: string };
  desks: FloorDesk[];
  total: number;
  free: number;
}
